import merge from "deepmerge";
import {
  ProductListingData,
  Breadcrumb,
  Facet,
  FacetDetail,
} from "@/types/productListing";
import { setJLBasketData } from "./setJLBasketData";
import { mapProduct } from "./mapProduct";
import { FoundItData, PLPFeatureFlags } from "@/types/types";
import { RouteParams } from "@/hooks/useRouteParams";
import { NextRouter } from "next/router";
import { TransformedDisplayPriceRange } from "@~/types/types";
import { formatFoundItLinksForAnalytics } from "./analyticsHelper";

type BasketProduct = {
  itemPrice: string;
  productId: string;
  qty: string;
  services: string[];
  sku: string;
};

type Search = {
  displayNum: number;
  filter: string;
  filterNvals: string;
  first6OutOfStock: string;
  first6ProductIDs: string;
  first6Promotions: string;
  first6Reviews: string;
  first6StarRatings: string;
  pageNum: string;
  pagination: number;
  preSelectedFilter: string;
  productIds: string[];
  redirectType: string;
  searchMethod: string;
  searchTermDidYouMean: string;
  searchTermRecommended: string;
  searchTerms: string;
  sort: string;
  totalNumberOfResults: number;
};

type Page = {
  locale: {
    country: string;
    currency: string;
    language: string;
  };
  pageInfo: {
    breadCrumb: { value: string; clickable: number }[];
    pageCanonicalURL: string;
    pageName: string;
    pageType: string;
    site: string;
    assignmentType?: string;
    categoryId?: string;
  };
  productPages: {
    productCardView: {
      cardsPerCol: number;
      checked: string;
    };
    foundItLinks?: string;
    products: {
      brand?: string;
      chunkDetails: {
        chunkOffset: number;
        personalisedPlp: boolean;
        products: {
          currency: string;
          isMultiSku: boolean;
          isOutOfStock: boolean;
          listPosition: number;
          mb: number;
          numColourSwatches: number;
          numReviews: number;
          priceNow: TransformedDisplayPriceRange;
          priceThen?: TransformedDisplayPriceRange;
          priceWas?: TransformedDisplayPriceRange;
          productId: string;
          rating: number;
          sku: string;
        }[];
      };
    };
  };
};

export type JlDataLayer = {
  basket?: {
    product: BasketProduct[];
    subTotal: string;
    totalNumberOfItems: number;
  };
  citrusProductsAds?: {
    citrusProductsAds: {
      adId: string;
      productId: string;
      type: string;
    }[];
  };
  compare?: { product: unknown[] };
  page: Page;
  search?: Search;
  vertex?: {
    vertexAttributionToken: string;
    vertexFilter: string;
  };
};

type GetJlDataProps = {
  productListingData: ProductListingData;
  flags: PLPFeatureFlags;
  route: RouteParams;
  router: NextRouter;
  windowWidth: number;
  hasUserClickedSimilarSearch?: boolean;
  foundItData?: FoundItData | null;
};

export type SelectedFacetProps = { facetName: string } & FacetDetail;

const ANALYTICS_SEPARATOR = "|";
const overwriteMerge = (_dest: never, source: never) => source;

const numCardsPerColumn = {
  DESKTOP: [4, 3],
  TABLET: [3, 2],
  MOBILE: [2, 1],
};

export const getAnalyticsPageType = (pageType: string, results = 0) => {
  switch (pageType) {
    case "search": {
      if (results > 0) {
        return "search results";
      }
      return "no search results";
    }
    case "brand":
    case "browse": {
      return "product list page grid";
    }
    default: {
      return "unknown";
    }
  }
};

export const getAnalyticsPageName = (
  pageType: string,
  crumbs: Breadcrumb[] = [],
) => {
  switch (pageType) {
    case "search": {
      return "jl:search";
    }
    case "brand":
    case "browse": {
      const displayNames =
        crumbs && crumbs.length > 0
          ? crumbs.map(({ displayName }) => displayName)
          : [];
      const crumbsList = displayNames.join(":").toLowerCase();
      return `jl:${crumbsList}:grid`;
    }
    default: {
      return "jl:unknown";
    }
  }
};

export const getFacetsWithSelectedValues = (
  facets: Facet[],
): SelectedFacetProps[] =>
  facets && facets.length > 0
    ? facets.flatMap(
        // ({ name, details }: { name: string; details: FacetDetail[] }) =>
        ({ name, details }) =>
          details
            .filter((details: FacetDetail) => details.isSelected === "true")
            .map((details: FacetDetail) => ({
              facetName: name,
              ...details,
            })),
      )
    : [];

export const getAppliedFilters = (selectedFacets: SelectedFacetProps[]) =>
  selectedFacets
    .map(({ facetName, label }) => `${facetName}:${label}`.toLowerCase())
    .join(ANALYTICS_SEPARATOR);

export const getAppliedFilterNvals = (selectedFacets: SelectedFacetProps[]) =>
  selectedFacets.map(({ trackingId }) => trackingId).join(ANALYTICS_SEPARATOR);

export const getBreadcrumbData = (crumbs: Breadcrumb[]) =>
  crumbs && crumbs.length > 0
    ? crumbs.map((crumb) => ({
        value: crumb.displayName,
        clickable: crumb.clickable === "true" ? 1 : 0,
      }))
    : [];

export const stripTagsFromSearchTerm = (searchTerm: string) => {
  if (!searchTerm) return "";

  const tags = ["#_search_suggestion_referral"];
  let cleanSearchTerm = searchTerm;

  tags.forEach((tag) => {
    cleanSearchTerm = searchTerm.replace(tag, "");
  });

  return cleanSearchTerm;
};

export const getSearchTerms = (
  crumbs: Breadcrumb[],
  searchTerm: string,
  isSuggestion: boolean,
) => {
  let searchTerms = stripTagsFromSearchTerm(searchTerm);

  if (searchTerms.length === 0 && isSuggestion && crumbs?.length > 0) {
    const [lastBreadcrumb] = crumbs.slice(-1);
    searchTerms = lastBreadcrumb ? lastBreadcrumb.displayName : "";
  }

  return searchTerms;
};

export const getScreenSize = (windowWidth: number) => {
  if (windowWidth < 568) {
    return "MOBILE";
  } else if (windowWidth < 768) {
    return "TABLET";
  } else {
    return "DESKTOP";
  }
};

export const setJLData = ({
  productListingData,
  flags,
  route,
  windowWidth,
  hasUserClickedSimilarSearch = false,
  foundItData,
}: GetJlDataProps) => {
  const { pageType } = route;
  const numericChunkSize = Number(flags.chunkSize?.value);
  const numericPageSize = Number(flags.pageSize?.value);
  const numericPage = Number(route.query.page || "1");

  if (!productListingData) return {};

  const basket = setJLBasketData();

  const sponsoredProducts =
    productListingData.products && productListingData.products.length > 0
      ? productListingData.products
          .filter((product) => Boolean(product.advertising))
          .map((product) => {
            const { associatedAdvertId, clickUrl, impressionUrl } =
              product.advertising || {};
            const isKevelProduct = clickUrl !== undefined;

            return {
              productId: product.productId,
              type: "sponsoredProduct",
              ...(!isKevelProduct ? { adId: associatedAdvertId } : {}),
              ...(isKevelProduct
                ? { kevelAdId: associatedAdvertId, clickUrl, impressionUrl }
                : {}),
            };
          })
      : [];

  const hasKevelSponsoredProducts = productListingData.products.some(
    (product) => Boolean(product.advertising?.clickUrl),
  );

  // This is a temporary solution to support both Citrus (pre-implementation) and Kevel (post-implementation) sponsored products.
  // Once we have fully migrated to Kevel, we can remove the Citrus sponsored products.  We will never received a mixed array
  // of Citrus and Kevel sponsored products, so we can safely check for the presence of a clickUrl to determine which type of sponsored products we have.
  const citrusProductAds =
    sponsoredProducts.length > 0 && !hasKevelSponsoredProducts
      ? { citrusProductAds: [...sponsoredProducts] }
      : {};
  const kevelProductAds =
    sponsoredProducts.length > 0 && hasKevelSponsoredProducts
      ? { kevelProductAds: [...sponsoredProducts] }
      : {};

  const compare = { product: [] };

  const selectedFacetValues = getFacetsWithSelectedValues(
    productListingData.facets,
  );
  const filter = getAppliedFilters(selectedFacetValues);
  const filterNvals = getAppliedFilterNvals(selectedFacetValues);

  const firstSixProducts =
    productListingData.products && productListingData.products.length > 0
      ? productListingData.products.slice(0, 6)
      : [];

  const slicedProducts = productListingData.products.length
    ? productListingData.products.slice(0, numericChunkSize)
    : [];

  const sharedInformation = {
    displayNum: slicedProducts.length,
    filter,
    filterNvals,
    first6OutOfStock: firstSixProducts
      .map((product) => (product.outOfStock ? "outofstock" : "instock"))
      .join(ANALYTICS_SEPARATOR),
    first6ProductIDs: firstSixProducts
      .map(({ productId }) => productId)
      .join(ANALYTICS_SEPARATOR),
    first6Promotions: firstSixProducts
      .map(
        ({ messaging }) =>
          messaging?.find((message) => message.type === "promotional")?.title ||
          "",
      )
      .join(ANALYTICS_SEPARATOR),
    first6Reviews: firstSixProducts
      .map(({ reviews }) => reviews)
      .join(ANALYTICS_SEPARATOR),
    first6StarRatings: firstSixProducts
      .map(({ averageRating }) => averageRating)
      .join(ANALYTICS_SEPARATOR),
    pageNum: `page ${numericPage}`,
    pagination: productListingData.results
      ? Math.ceil(productListingData.results / numericPageSize)
      : 0,
    productIds: slicedProducts.map(({ productId }) => productId),
    sort:
      (Array.isArray(route.query.sortBy)
        ? route.query.sortBy[0]
        : route.query.sortBy) || "default: relevance",
    totalNumberOfResults: productListingData.results || 0,
  };

  const originalSearchTerm = route.query.originalSearchTerm
    ? {
        searchMethod: "search redirect",
        searchTerms: stripTagsFromSearchTerm(
          decodeURIComponent(route.query.originalSearchTerm as string),
        ),
      }
    : {};

  const brandParameters =
    pageType === "brand"
      ? {
          brand: (
            productListingData.pageInformation.brandName || ""
          ).toLowerCase(),
        }
      : {};

  const categoryParameters =
    pageType === "browse" &&
    productListingData.browseInformation?.category.assignmentType
      ? {
          assignmentType:
            productListingData.browseInformation?.category.assignmentType,
          categoryId: productListingData.browseInformation?.category.categoryId,
        }
      : {};

  const screenSize = getScreenSize(windowWidth);

  const isLargeCardSize = flags.isLargeCardSizeFlagOn?.value === "true";

  const search = {
    preSelectedFilter: "",
    redirectType: productListingData.redirectType || "no redirect",
    searchMethod: hasUserClickedSimilarSearch
      ? "Also searched"
      : "typed search",
    searchTermDidYouMean: productListingData.correction
      ?.alternativeSearchSuggestions
      ? productListingData.correction?.alternativeSearchSuggestions
          .map(({ searchTerm }) => searchTerm)
          .join(ANALYTICS_SEPARATOR)
      : "",
    searchTermRecommended:
      productListingData.correction?.correctedSearchTerm || "",
    searchTerms: getSearchTerms(
      productListingData.crumbs,
      route.query["search-term"] as string,
      route.query.suggestion === "true",
    ),
    ...sharedInformation,
    ...originalSearchTerm,
  };

  const page: Page = {
    locale: {
      country: "uk",
      currency: "gbp",
      language: "en-gb",
    },
    pageInfo: {
      breadCrumb: getBreadcrumbData(productListingData.crumbs),
      pageCanonicalURL:
        typeof window !== "undefined" ? window.location.href : "",
      pageName: getAnalyticsPageName(pageType, productListingData.crumbs), // Needs to be "jl:comparison page" for Compare
      pageType: getAnalyticsPageType(pageType, productListingData.results), // Needs to be 'comparison overlay' for Compare
      site: "m.johnlewis.com",
      ...categoryParameters,
    },
    productPages: {
      productCardView: {
        cardsPerCol: numCardsPerColumn[screenSize][
          Number(isLargeCardSize)
        ] as number,
        checked: isLargeCardSize ? "min" : "max",
      },
      products: {
        ...brandParameters,
        chunkDetails: {
          chunkOffset: 0,
          personalisedPlp: productListingData.isPersonalised || false,
          products: productListingData.products
            ? productListingData.products.map(mapProduct)
            : [],
        },
        ...(["brand", "browse"].includes(pageType) ? sharedInformation : {}),
      },
    },
    ...(pageType === "search" || route.query.suggestion === "true"
      ? { search: { ...search } }
      : Object.keys(originalSearchTerm).length > 0
        ? { search: { ...originalSearchTerm } }
        : {}),
  };

  const foundItLinkString = formatFoundItLinksForAnalytics(
    foundItData?.carousel || foundItData?.links || [],
  );
  page.productPages.foundItLinks = foundItLinkString;

  //------------------------------------------------------------------
  // 5. vertex
  //------------------------------------------------------------------
  const hasVertexData = productListingData.analytics
    ? Object.keys(productListingData.analytics).length > 0
    : false;

  const { attributionToken: vertexAttributionToken, filter: vertexFilter } =
    productListingData.analytics || {};

  const vertex = hasVertexData
    ? {
        vertex: {
          vertexAttributionToken,
          vertexFilter,
          vertexProductOffset: (numericPage - 1) * numericPageSize,
        },
      }
    : {};

  const components = [
    { basket: { ...basket } },
    { ...citrusProductAds },
    { ...kevelProductAds },
    { compare: { ...compare } },
    { page: { ...page } },
    { ...vertex },
  ];

  window.jlData = merge.all([window.jlData || {}, ...components], {
    arrayMerge: overwriteMerge,
  });

  // merge doesn't overwrite if source object is empty so this removes any sponsoredProduct ads that should no longer be there
  if (
    Object.keys(citrusProductAds).length === 0 &&
    window.jlData?.citrusProductAds
  ) {
    delete window.jlData.citrusProductAds;
  }
  if (
    Object.keys(kevelProductAds).length === 0 &&
    window.jlData?.kevelProductAds
  ) {
    delete window.jlData.kevelProductAds;
  }

  if (!hasVertexData && window.jlData?.vertex) {
    delete window.jlData.vertex;
  }

  return window.jlData;
};
